'use client';

import { useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Text,
  Button,
  Textarea,
  Tooltip,
  IconButton,
} from '@terra/ui-design-system';
import { BookmarkAdd01Icon } from '@terra/ui-icons';
import { useUserPreferences } from '../../hooks/useUserPreferences';

type UseChatSavePreferenceReturn = {
  isOpen: boolean;
  isSaving: boolean;
  content: string;
  setContent: (content: string) => void;
  open: () => void;
  close: () => void;
  handleSave: () => Promise<void>;
};

export function useChatSavePreference(
  applicationId: string | undefined,
): UseChatSavePreferenceReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [content, setContent] = useState('');
  const { createPreference } = useUserPreferences({
    applicationId,
    enabled: false,
    entryPoint: 'torch_chat_preference',
  });
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    setContent('');
  }, []);
  const handleSave = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const success = await createPreference(content);

      if (success) {
        setIsOpen(false);
        setContent('');
      }
    } finally {
      setIsSaving(false);
    }
  }, [content, createPreference, isSaving]);

  return { isOpen, isSaving, content, setContent, open, close, handleSave };
}

type TriggerProps = {
  isLoading: boolean;
  onOpen: () => void;
};

export function ChatSavePreferenceTrigger({ isLoading, onOpen }: TriggerProps) {
  const tCopilot = useTranslations('chat.copilot');

  return (
    <Tooltip
      title={tCopilot('savePreferenceTooltip')}
      positioning={{ strategy: 'fixed', placement: 'top' }}
    >
      <IconButton
        type="button"
        variant="ghost"
        aria-label={tCopilot('savePreferenceAriaLabel')}
        onClick={onOpen}
        disabled={isLoading}
        color="fg.muted"
        _hover={{ color: 'fg.default', bg: 'transparent' }}
        _disabled={{ color: 'fg.subtle', cursor: 'not-allowed' }}
        size="xs"
        w="7"
        h="7"
      >
        <BookmarkAdd01Icon size={16} />
      </IconButton>
    </Tooltip>
  );
}

type PanelProps = {
  isOpen: boolean;
  isSaving: boolean;
  content: string;
  onContentChange: (content: string) => void;
  onClose: () => void;
  onSave: () => Promise<void>;
};

export function ChatSavePreferencePanel({
  isOpen,
  isSaving,
  content,
  onContentChange,
  onClose,
  onSave,
}: PanelProps) {
  const tCopilot = useTranslations('chat.copilot');

  if (!isOpen) return null;

  return (
    <Box
      position="absolute"
      bottom="70px"
      left="12px"
      right="12px"
      zIndex={100}
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      p={3}
      bg="bg.subtle"
      boxShadow="md"
    >
      <VStack gap={2} w="full" alignItems="flex-start">
        <VStack gap="0.5" alignItems="flex-start" w="full">
          <Text fontSize="xs" fontWeight="medium">
            {tCopilot('savePreferenceTitle')}
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {tCopilot('savePreferenceDescription')}
          </Text>
        </VStack>
        <Textarea
          size="sm"
          value={content}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onContentChange(e.target.value)
          }
          placeholder={tCopilot('savePreferencePlaceholder')}
          rows={3}
          resize="vertical"
          autoFocus
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Escape') onClose();
          }}
        />
        <HStack gap={2} justifyContent="flex-end" w="full">
          <Button
            size="xs"
            variant="ghost"
            disabled={isSaving}
            onClick={onClose}
          >
            {tCopilot('cancel')}
          </Button>
          <Button
            size="xs"
            variant="outline"
            disabled={!content.trim() || isSaving}
            loading={isSaving}
            onClick={onSave}
          >
            {tCopilot('savePreferenceButton')}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
